import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import {
  PiCalendarStar,
  PiTag,
  PiListBullets,
  PiHandshake,
  PiEnvelopeOpen,
  PiNotepad,
  PiHandCoins,
  PiCalendarBlank,
  PiCaretLeft,
  PiCaretDown,
  PiCaretUp,
  PiCheck,
  PiMagnifyingGlass,
  PiX,
  PiClockCounterClockwise,
} from 'react-icons/pi'
import { useInfiniteQuery } from '@tanstack/react-query'
import { useDebounce } from 'use-debounce'
import { format, differenceInDays } from 'date-fns'
import { MobileColors } from 'styles/mobileTokens'
import { getDaysSinceHex } from 'utils/formatting'
import { BackButton } from 'components/MobileSearch/MobileSearch.styles'
import { groupEventsByDate } from 'components/NewCompanyPage/EventHistory/utils'
import { EventIcon } from 'components/NewCompanyPage/EventHistory/EventIcon'
import { formatEventTime } from 'components/NewCompanyPage/EventHistory/utils'
import CellEventFormatterV2 from 'components/Tables/ActivityTableV2/CellEvent/CellEventFormatterV2'
import useApiService from 'services/api'
import { Spinner } from 'react-bootstrap'
import { ActivityListResponseV2, ActivityV2 } from 'models/activityV2'
import { Popover, PopoverContent, PopoverTrigger } from 'components/UI/popover'
import { Calendar } from 'components/UI/calendar'
import { dataTableSortingStoreRepo } from 'components/DataTable/DataTableSorting/DataTableSortingStore'
import { SortingOptionLabel } from 'components/SortSelect/SortingOptionLabel'
import { MOBILE_EVENTS_TABLE_KEY } from 'constants/tableQueryKeys'
import { cn } from 'components/UI/cn'

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: white;
`

const HeaderSection = styled.div`
  padding: 12px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f9fafb;
`

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const HeaderTitle = styled.h1`
  font-size: 14px;
  font-weight: 400;
  margin: 0;
  color: #191d23;
  display: flex;
  align-items: center;
  gap: 6px;
`

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const UpdatedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  color: #6e6e6e;
`

const DaysBadge = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 6px;
  background: #fafafa;
  border: 1px solid #e2e2e2;
  border-radius: 999px;
  font-size: 10px;
  color: #191d23;

  &::before {
    content: '';
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${(props) => props.$color};
  }
`

const FiltersSection = styled.div`
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #f9fafb;
`

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
`

const SearchInput = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  flex: 1;

  input {
    flex: 1;
    border: none;
    outline: none;
    font-size: 14px;
    color: #374151;
    background: transparent;

    &::placeholder {
      color: #9ca3af;
    }
  }

  svg {
    color: #9ca3af;
    flex-shrink: 0;
  }
`

const ListArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px;
`

const DateGroupContainer = styled.div`
  margin-bottom: 16px;
`

const DateGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`

// Individual event card with green left bar
const EventCard = styled.div`
  display: flex;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`

const GreenBar = styled.div`
  width: 4px;
  background: #c8e6c9;
  flex-shrink: 0;
`

const EventCardContent = styled.div`
  flex: 1;
  padding: 10px 12px;
  position: relative;
`

const EventHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 4px;
`

const EventDateTime = styled.div`
  font-size: 12px;
  color: #6b7280;
`

const EventIconContainer = styled.div`
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #f0f9f4;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #e8f5ec;
`

const EventText = styled.div`
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;

  .user-name {
    color: ${MobileColors.primary};
    font-weight: 600;
  }
`

const MoreLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
  width: 100%;
  padding: 8px 0 0;
  background: transparent;
  border: none;
  font-size: 12px;
  color: ${MobileColors.primary};
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`

const DropdownButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #e2e2e2;
  border-radius: 8px;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  width: 100%;
  justify-content: space-between;

  &:hover {
    border-color: ${MobileColors.primary};
  }
`

const DropdownItem = styled.button<{ $isSelected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 14px;
  background: ${(props) => (props.$isSelected ? '#f3f4f6' : 'transparent')};
  border: none;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f9fafb;
  }

  svg {
    width: 18px;
    height: 18px;
    color: #6b7280;
  }
`

const CheckContainer = styled.div`
  margin-left: auto;
  color: #2c6b4e;
`

type SortOption = {
  label: ReactNode
  value: string
}

const EVENTS_SORTING_OPTIONS: SortOption[] = [
  {
    label: <SortingOptionLabel label="Date" type="number" desc />,
    value: '-created_at',
  },
  {
    label: <SortingOptionLabel label="Date" type="number" />,
    value: 'created_at',
  },
  {
    label: <SortingOptionLabel label="Team Member" />,
    value: 'user_name',
  },
  {
    label: <SortingOptionLabel label="Team Member" desc />,
    value: '-user_name',
  },
  {
    label: <SortingOptionLabel label="Event Type" />,
    value: 'content_type',
  },
  {
    label: <SortingOptionLabel label="Event Type" desc />,
    value: '-content_type',
  },
]

type EventTypeFilter =
  | 'all'
  | 'deal'
  | 'email'
  | 'notes'
  | 'samples'
  | 'reminder'
  | 'contactcompany'

interface EventTypeOption {
  value: EventTypeFilter
  label: string
  icon: React.ElementType
}

const EVENT_TYPE_OPTIONS: EventTypeOption[] = [
  { value: 'all', label: 'All', icon: PiListBullets },
  { value: 'deal', label: 'Deals', icon: PiHandshake },
  { value: 'email', label: 'Emails', icon: PiEnvelopeOpen },
  { value: 'notes', label: 'Notes', icon: PiNotepad },
  { value: 'samples', label: 'Samples', icon: PiHandCoins },
  { value: 'reminder', label: 'Reminders', icon: PiCalendarStar },
  { value: 'contactcompany', label: 'Account Attributes', icon: PiTag },
]

// Mobile Event Date Group component with expand/collapse
function MobileEventDateGroup({
  group,
}: {
  group: {
    date: string
    relativeDate: string
    displayDate: string
    events: ActivityV2[]
  }
}) {
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMoreThan3Events = group.events.length > 3
  const eventsToShow = isExpanded ? group.events : group.events.slice(0, 3)

  return (
    <DateGroupContainer>
      {/* Date group label */}
      <DateGroupLabel>{group.relativeDate}</DateGroupLabel>

      {/* Individual event cards */}
      {eventsToShow.map((event, idx) => (
        <EventCard key={`${event.created_at}-${idx}`}>
          <GreenBar />
          <EventCardContent>
            <EventHeader>
              <EventDateTime>
                {group.displayDate} {formatEventTime(event.created_at)}
              </EventDateTime>
              <EventIconContainer>
                <EventIcon
                  contentType={event.content_type}
                  actionType={event.action_type}
                />
              </EventIconContainer>
            </EventHeader>
            <EventText>
              <span className="user-name">{event.user_name}</span>{' '}
              <CellEventFormatterV2 event={event} />
            </EventText>
          </EventCardContent>
        </EventCard>
      ))}

      {/* More/Less link */}
      {hasMoreThan3Events && (
        <MoreLink onClick={() => setIsExpanded(!isExpanded)}>
          <span>
            {isExpanded ? 'Less' : `More (+${group.events.length - 3})`}
          </span>
          <PiCaretUp
            className={`w-4 h-4 transition-transform ${
              isExpanded ? '' : 'rotate-180'
            }`}
          />
        </MoreLink>
      )}
    </DateGroupContainer>
  )
}

export function MobileEventsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const api = useApiService()

  // Query params
  const contactCompanyId = searchParams.get('contact_company_id')
  const doorProxyId = searchParams.get('door_proxy_id')

  // Sorting store (persisted, same pattern as reminders/samples)
  const sortingStore = dataTableSortingStoreRepo.getStore(
    MOBILE_EVENTS_TABLE_KEY
  )()
  const { sorting, setSorting } = sortingStore

  // Filter states
  const [selectedEventType, setSelectedEventType] =
    useState<EventTypeFilter>('all')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500)
  const [dateRange, setDateRange] = useState<
    { from: Date; to?: Date } | undefined
  >(undefined)

  // Compute sort param from sorting store (mirrors ActivityTableV2 pattern,
  // uses the `sort` param expected by BaseRawSQLAPIView / RawSQLFilter)
  const sortParam = useMemo(() => {
    if (!sorting?.length) return undefined
    return sorting[0].desc ? '-' + sorting[0].id : sorting[0].id
  }, [sorting])

  // Build query params
  const buildQueryParams = (pageParam: number) => {
    const baseParams: Record<string, unknown> = {
      page: pageParam,
      page_size: 20,
    }

    if (doorProxyId) {
      baseParams.door_proxy_id = doorProxyId
    }

    if (selectedEventType !== 'all') {
      baseParams.event_type = selectedEventType
    }

    if (debouncedSearchTerm.trim()) {
      baseParams.search = debouncedSearchTerm.trim()
    }

    if (dateRange?.from) {
      baseParams.created_at_greater = dateRange.from.toISOString()
    }
    if (dateRange?.to) {
      baseParams.created_at_less = dateRange.to.toISOString()
    }

    if (sortParam) {
      baseParams.sort = sortParam
    }

    return baseParams
  }

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: [
      'mobileEventsPage',
      contactCompanyId,
      doorProxyId,
      selectedEventType,
      debouncedSearchTerm,
      dateRange,
      sortParam,
    ],
    queryFn: ({ pageParam }) =>
      api.getCompanyActivityList(
        Number(contactCompanyId),
        buildQueryParams(pageParam)
      ),
    initialPageParam: 1,
    getNextPageParam: (lastPage: ActivityListResponseV2, allPages) => {
      const totalFetched = allPages.reduce(
        (sum, page) => sum + page.results.length,
        0
      )
      if (totalFetched < lastPage.count) {
        return allPages.length + 1
      }
      return undefined
    },
    enabled: !!contactCompanyId,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  })

  // Flatten and group events
  const groupedEvents = useMemo(() => {
    if (!data?.pages) return []
    const allEvents = data.pages.flatMap((page) => page.results)

    // Filter out noise events. Text search is handled server-side via the
    // `search` query param (see buildQueryParams), matching the desktop
    // event-history query.
    const filteredEvents = allEvents.filter((event) => {
      // Filter out document events with empty field_history_objects
      if (event.content_type?.model === 'document') {
        return (
          event.field_history_objects && event.field_history_objects.length > 0
        )
      }
      // Filter out email UPDATE events
      if (
        event.content_type?.model === 'emailmessage' &&
        event.action_type === 'UPDATE'
      ) {
        return false
      }
      return true
    })

    return groupEventsByDate(filteredEvents)
  }, [data?.pages])

  // Get the most recent event for header display
  const mostRecentEvent = useMemo(() => {
    if (!data?.pages?.[0]?.results?.[0]) return null
    return data.pages[0].results[0]
  }, [data?.pages])

  // Calculate days since last update
  const daysSinceUpdate = useMemo(() => {
    if (!mostRecentEvent?.created_at) return 0
    return differenceInDays(new Date(), new Date(mostRecentEvent.created_at))
  }, [mostRecentEvent])

  const handleBack = () => {
    navigate(-1)
  }

  const handleSelectEventType = (value: EventTypeFilter) => {
    setSelectedEventType(value)
    setIsDropdownOpen(false)
  }

  const selectedOption = EVENT_TYPE_OPTIONS.find(
    (opt) => opt.value === selectedEventType
  )

  const selectedSort =
    (sortParam
      ? EVENTS_SORTING_OPTIONS.find((o) => o.value === sortParam)
      : undefined) ?? EVENTS_SORTING_OPTIONS[0]

  const handleSelectSort = (value: string) => {
    const isDesc = value.startsWith('-')
    const id = isDesc ? value.slice(1) : value
    setSorting([{ id, desc: isDesc }])
    setIsSortOpen(false)
  }

  return (
    <PageContainer>
      <BackButton onClick={handleBack}>
        <PiCaretLeft size={20} />
        Back
      </BackButton>

      {/* Header with title and update info */}
      <HeaderSection>
        <HeaderLeft>
          <HeaderTitle>
            <PiClockCounterClockwise size={16} />
            EVENT HISTORY
          </HeaderTitle>
        </HeaderLeft>
        {mostRecentEvent && (
          <HeaderRight>
            <UpdatedBadge>
              <PiCalendarBlank size={12} />
              Updated{' '}
              {format(new Date(mostRecentEvent.created_at), 'MM/dd/yyyy')}
            </UpdatedBadge>
            <DaysBadge $color={getDaysSinceHex(daysSinceUpdate)}>
              <span style={{ fontWeight: 600 }}>{daysSinceUpdate}</span>{' '}
              {daysSinceUpdate === 1 ? 'day' : 'days'}
            </DaysBadge>
          </HeaderRight>
        )}
      </HeaderSection>

      {/* Filters Section */}
      <FiltersSection>
        {/* Search Input */}
        <SearchInput>
          <PiMagnifyingGlass size={16} />
          <input
            type="text"
            placeholder="Search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <PiX
              size={16}
              style={{ cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            />
          )}
        </SearchInput>

        <div
          className={cn(
            'grid gap-[8px] grid-cols-3',
            !!dateRange && 'grid-cols-2'
          )}
        >
          {/* Event Type Dropdown */}
          <div className="flex-1">
            <Popover open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
              <PopoverTrigger asChild>
                <DropdownButton>
                  <span className="flex items-center gap-2">
                    {selectedOption && <selectedOption.icon size={18} />}
                    {selectedOption?.label || 'All'}
                  </span>
                  <PiCaretDown size={18} />
                </DropdownButton>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto min-w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
              >
                {EVENT_TYPE_OPTIONS.map((option) => {
                  const Icon = option.icon
                  const isSelected = option.value === selectedEventType
                  return (
                    <DropdownItem
                      key={option.value}
                      $isSelected={isSelected}
                      onClick={() => handleSelectEventType(option.value)}
                    >
                      <Icon />
                      {option.label}
                      {isSelected && (
                        <CheckContainer>
                          <PiCheck size={16} />
                        </CheckContainer>
                      )}
                    </DropdownItem>
                  )
                })}
              </PopoverContent>
            </Popover>
          </div>

          {/* Date Range Filter */}
          <FilterRow className={cn(!!dateRange && 'col-span-full row-start-2')}>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="w-full flex items-center gap-2 whitespace-nowrap rounded-lg border border-line bg-surface px-3 py-[10px] text-[14px] text-fg hover:border-brand"
                >
                  <PiCalendarBlank
                    size={18}
                    className="shrink-0 text-fg-tertiary"
                  />
                  <span>
                    {dateRange?.from ? (
                      dateRange.to ? (
                        <>
                          {format(dateRange.from, 'd MMM yyyy')} -{' '}
                          {format(dateRange.to, 'd MMM yyyy')}
                        </>
                      ) : (
                        format(dateRange.from, 'd MMM yyyy')
                      )
                    ) : (
                      'All time'
                    )}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  initialFocus
                  mode="range"
                  defaultMonth={dateRange?.from}
                  selected={dateRange}
                  onSelect={(newRange) => {
                    if (!newRange?.from) {
                      return
                    }
                    setDateRange(newRange as { from: Date; to?: Date })
                  }}
                  numberOfMonths={1}
                />
              </PopoverContent>
            </Popover>
            {dateRange && (
              <button
                type="button"
                aria-label="Clear date range"
                onClick={() => setDateRange(undefined)}
                className="flex shrink-0 items-center justify-center rounded-lg border border-line bg-surface px-2.5 text-fg-tertiary hover:border-brand hover:text-fg-secondary aspect-square"
              >
                <PiX size={14} />
              </button>
            )}
          </FilterRow>

          {/* Sort Control */}
          <div className="flex-1">
            <Popover open={isSortOpen} onOpenChange={setIsSortOpen}>
              <PopoverTrigger asChild>
                <DropdownButton>
                  <span className="flex items-center gap-2">
                    {selectedSort.label}
                  </span>
                  <PiCaretDown size={18} />
                </DropdownButton>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-auto min-w-[var(--radix-popover-trigger-width)] overflow-hidden p-0"
              >
                {EVENTS_SORTING_OPTIONS.map((option) => {
                  const isSelected = option.value === selectedSort.value
                  return (
                    <DropdownItem
                      key={option.value}
                      $isSelected={isSelected}
                      onClick={() => handleSelectSort(option.value)}
                    >
                      {option.label}
                      {isSelected && (
                        <CheckContainer>
                          <PiCheck size={16} />
                        </CheckContainer>
                      )}
                    </DropdownItem>
                  )
                })}
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </FiltersSection>

      <ListArea>
        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Spinner animation="border" size="sm" />
          </div>
        )}

        {/* Error state */}
        {!!error && (
          <div className="py-8 text-center text-sm text-red-500">
            Failed to load event history
          </div>
        )}

        {/* Empty state */}
        {!error && groupedEvents.length === 0 && !isLoading && (
          <div className="py-8 text-center text-sm text-gray-500">
            No events found
          </div>
        )}

        {/* Content */}
        {!error && groupedEvents.length > 0 && (
          <div style={{ paddingBottom: 48 }}>
            {groupedEvents.map((group) => (
              <MobileEventDateGroup key={group.date} group={group} />
            ))}

            {/* Load More Button */}
            {hasNextPage && (
              <div className="flex justify-center pt-2 pb-4">
                <button
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="px-4 py-2 text-sm bg-fb-dark-green text-white rounded hover:bg-fb-dark-green-hover transition-colors disabled:opacity-50"
                >
                  {isFetchingNextPage ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        )}
      </ListArea>
    </PageContainer>
  )
}
